import type { AuthenticatedUser, IResource } from "@gettactic/api";
import { isBridgestone } from "../featureFlags";
import { isOrganizationAdmin } from "../features/isOrganizationAdmin";

export function canRespondOthers(authenticatedUser: AuthenticatedUser) {
  return !!authenticatedUser?.user?.permissions.find(
    (x) => x === "schedule:respond:manage" || x === "schedule:respond"
  );
}

export function canReadActionLog(authenticatedUser: AuthenticatedUser) {
  return !!authenticatedUser?.user?.permissions?.includes(
    "organization:action:read"
  );
}

export function canViewDirectoryChanges(authenticatedUser: AuthenticatedUser) {
  return (
    isBridgestone(authenticatedUser) && canReadActionLog(authenticatedUser)
  );
}

export function canScheduleOthers(authenticatedUser: AuthenticatedUser) {
  return (
    !!authenticatedUser?.permissions?.schedule_others ||
    !!authenticatedUser?.permissions?.schedule_others_managed
  );
}

/**
 * Whether the person looks after resources rather than merely booking them:
 * an organization settings admin, or someone holding `resource:manage`. This
 * is the coarse test. Somewhere a particular resource is in hand, narrow it
 * further to the ones that person may actually edit.
 */
export function canManageResources(authenticatedUser: AuthenticatedUser) {
  return (
    !!authenticatedUser?.permissions?.settings ||
    (authenticatedUser?.user?.organization_user?.permissions ?? []).includes(
      "resource:manage"
    )
  );
}

/**
 * Whether the person may manage this particular resource: an organization
 * settings admin, or anyone the API marks with `capabilities.can_manage` (a resource
 * manager of it, or a manager of its zone).
 */
export function canManageResource(
  authenticatedUser: AuthenticatedUser,
  resource: Pick<IResource, "capabilities"> | null | undefined
): boolean {
  return (
    !!authenticatedUser?.permissions?.settings ||
    !!resource?.capabilities?.can_manage
  );
}

export function isBillingVisible(authenticatedUser: AuthenticatedUser) {
  const isOwner = isOrganizationAdmin(authenticatedUser);
  return (
    isOwner ||
    (authenticatedUser?.user?.permissions?.includes("billing:read") ?? false)
  );
}
